import React, { useMemo } from "react"
import { WingsCompanyCard } from "@wingspans-ui"
import { useIntl } from "react-intl"
import { JsonParam, useQueryParam } from "use-query-params"
import { useAllCompanies } from "../../data/graphql"
import {
  localizeIndustryCheckboxTranslations,
  localizeSizeCheckboxTranslations,
} from "../algolia/localized-algolia-checkboxes"
import ExploreUnifiedFiltersDropdown from "./ExploreUnifiedFiltersDropdown"
import LocalExplore from "./LocalExplore"

const COMPANIES_PER_PAGE = 9

const FILTER_KEYS = {
  size: "company_size_filter",
  industry: "company_industry_filter",
}

const REFINEMENT_FIELDS = [FILTER_KEYS.size, FILTER_KEYS.industry]
const FILTER_ID_SEPARATOR = "::"
const ENABLE_EXCLUSIVE_OPTION_HIDING = false

const companiesSearchKeys = [
  { name: "title", weight: 0.55 },
  { name: "company_location", weight: 0.25 },
  { name: "company_industry_text", weight: 0.2 },
]

const normalizeRefinementList = (refinementList) => {
  const normalized = {
    [FILTER_KEYS.size]: [],
    [FILTER_KEYS.industry]: [],
  }

  if (!refinementList || typeof refinementList !== "object") {
    return normalized
  }

  Object.keys(normalized).forEach((key) => {
    const values = refinementList[key]
    if (Array.isArray(values)) {
      normalized[key] = values
      return
    }

    normalized[key] = typeof values === "string" && values ? [values] : []
  })

  return normalized
}

const getLocalizedLabel = (value, translationMap, locale) => {
  return translationMap?.[value]?.[locale] || value
}

const toArray = (value) => {
  if (!value) {
    return []
  }

  return Array.isArray(value) ? value.filter(Boolean) : [value]
}

const matchesSelection = (value, selectedValues) => {
  if (!selectedValues.length) {
    return true
  }

  return toArray(value).some((item) => selectedValues.includes(item))
}

const companyMatchesRefinements = (company, refinementList) => {
  return matchesSelection(company[FILTER_KEYS.size], refinementList[FILTER_KEYS.size])
    && matchesSelection(company[FILTER_KEYS.industry], refinementList[FILTER_KEYS.industry])
}

const getFilterCounts = (items, field, translationMap, locale) => {
  const counts = new Map()

  items.forEach((item) => {
    toArray(item[field]).forEach((value) => {
      counts.set(value, (counts.get(value) || 0) + 1)
    })
  })

  return Array.from(counts.entries())
    .map(([value, count]) => ({
      value,
      label: getLocalizedLabel(value, translationMap, locale),
      count,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

const ensureSelectedOptionsVisible = (options, selectedValues, translationMap, locale) => {
  const optionMap = new Map(options.map((option) => [option.value, option]))

  selectedValues.forEach((value) => {
    if (!optionMap.has(value)) {
      optionMap.set(value, {
        value,
        label: getLocalizedLabel(value, translationMap, locale),
        count: 0,
      })
    }
  })

  return Array.from(optionMap.values()).sort((a, b) => a.label.localeCompare(b.label))
}

const mapCompanyToCard = (company) => {
  return (
    <WingsCompanyCard
      objectID={company.id}
      img={company.company_logo}
      title={company.title}
      slug={company.slug}
      location={company.company_location}
      contentType={company.type}
      industries={company.company_industry_filter}
      sizes={company.company_size_filter}
      employeeCount={company.company_number_employees}
    />
  )
}

const CompaniesExplore = ({ classContext = "companies", filterList }) => {
  const { locale, formatMessage } = useIntl()
  const [queryFilters, setQueryFilters] = useQueryParam("queryFilters", JsonParam)
  const companies = useAllCompanies(locale)

  const companiesWithSearchText = companies.map((company) => ({
    ...company,
    company_industry_text: Array.isArray(company.company_industry_filter)
      ? company.company_industry_filter.join(" ")
      : (company.company_industry_filter || ""),
  }))

  const selectedRefinements = useMemo(() => {
    return normalizeRefinementList(queryFilters?.refinementList)
  }, [queryFilters])

  const getCompaniesMatchingOtherRefinements = (excludedField) => {
    const refinementListWithoutField = REFINEMENT_FIELDS.reduce((acc, field) => {
      acc[field] = field === excludedField ? [] : (selectedRefinements[field] || [])
      return acc
    }, {})

    return companiesWithSearchText.filter((company) => companyMatchesRefinements(company, refinementListWithoutField))
  }

  const sizeOptions = useMemo(() => {
    const matchingCompanies = ENABLE_EXCLUSIVE_OPTION_HIDING
      ? getCompaniesMatchingOtherRefinements(FILTER_KEYS.size)
      : companiesWithSearchText
    const options = getFilterCounts(matchingCompanies, FILTER_KEYS.size, localizeSizeCheckboxTranslations, locale)

    return ensureSelectedOptionsVisible(
      options,
      selectedRefinements[FILTER_KEYS.size],
      localizeSizeCheckboxTranslations,
      locale
    )
  }, [companiesWithSearchText, selectedRefinements, locale])

  const industryOptions = useMemo(() => {
    const matchingCompanies = ENABLE_EXCLUSIVE_OPTION_HIDING
      ? getCompaniesMatchingOtherRefinements(FILTER_KEYS.industry)
      : companiesWithSearchText
    const options = getFilterCounts(matchingCompanies, FILTER_KEYS.industry, localizeIndustryCheckboxTranslations, locale)

    return ensureSelectedOptionsVisible(
      options,
      selectedRefinements[FILTER_KEYS.industry],
      localizeIndustryCheckboxTranslations,
      locale
    )
  }, [companiesWithSearchText, selectedRefinements, locale])

  const filteredCompanies = useMemo(() => {
    return companiesWithSearchText.filter((company) => companyMatchesRefinements(company, selectedRefinements))
  }, [companiesWithSearchText, selectedRefinements])

  const toggleRefinement = (filterKey, value, isChecked) => {
    const currentRefinements = normalizeRefinementList(queryFilters?.refinementList)
    const currentValues = currentRefinements[filterKey] || []
    const nextValues = isChecked
      ? [...new Set([...currentValues, value])]
      : currentValues.filter((item) => item !== value)

    setQueryFilters({
      ...(queryFilters || {}),
      page: 1,
      refinementList: {
        ...currentRefinements,
        [filterKey]: nextValues,
      },
    })
  }

  const clearRefinements = () => {
    setQueryFilters({
      ...(queryFilters || {}),
      page: 1,
      refinementList: {},
    })
  }

  const selectedFilterOptionIds = useMemo(() => {
    return Object.entries(selectedRefinements).flatMap(([filterKey, values]) => {
      return (values || []).map((value) => `${filterKey}${FILTER_ID_SEPARATOR}${value}`)
    })
  }, [selectedRefinements])

  const filterSections = useMemo(() => {
    return [
      {
        id: FILTER_KEYS.size,
        title: formatMessage({ id: "companies-explore-size", defaultMessage: "Size" }),
        columns: 3,
        options: sizeOptions.map((option) => ({
          id: `${FILTER_KEYS.size}${FILTER_ID_SEPARATOR}${option.value}`,
          label: option.label,
        })),
      },
      {
        id: FILTER_KEYS.industry,
        title: formatMessage({ id: "companies-explore-industry", defaultMessage: "Industry" }),
        columns: 3,
        options: industryOptions.map((option) => ({
          id: `${FILTER_KEYS.industry}${FILTER_ID_SEPARATOR}${option.value}`,
          label: option.label,
        })),
      },
    ]
  }, [formatMessage, sizeOptions, industryOptions])

  const handleUnifiedFilterToggle = (optionId) => {
    const [filterKey, value] = optionId.split(FILTER_ID_SEPARATOR)

    if (!filterKey || !value) {
      return
    }

    const isChecked = selectedRefinements[filterKey]?.includes(value)
    toggleRefinement(filterKey, value, !isChecked)
  }

  return (
    <LocalExplore
      classContext={classContext}
      filterList={filterList}
      items={filteredCompanies}
      searchKeys={companiesSearchKeys}
      hitsPerPage={COMPANIES_PER_PAGE}
      searchPlaceholder={formatMessage({
        id: "algolia-companies-explore-search-placeholder",
        defaultMessage: "Search by company name, location, industry",
      })}
      renderCard={mapCompanyToCard}
      renderSortExtras={() => {
        return (
          <ExploreUnifiedFiltersDropdown
            heading={formatMessage({ id: "companies-explore-filter-companies-by", defaultMessage: "Filter Featured Employers by" })}
            viewLabel={formatMessage({ id: "algolia-toggle-dropdown-companies", defaultMessage: "Featured Employers" })}
            sections={filterSections}
            selectedOptionIds={selectedFilterOptionIds}
            onToggleOption={handleUnifiedFilterToggle}
            onClearAll={clearRefinements}
          />
        )
      }}
    />
  )
}

export default CompaniesExplore
